import { LitElement, css, html } from 'lit';
import { TopicCategory, UserTag } from '../../types';
import { customElement, property, state } from 'lit/decorators';
import { instanceOfTagGroup, moveItem } from '../../helpers/tags-categories';

import { cancelDefault } from '../../helpers/utils';
import { getVisibleTextColor } from '../../helpers/colors';

@customElement('tags-categories-wrapper')
export class TagsCategoriesWrapper extends LitElement {
    static amplitude_callsite: string = 'Edit Categories Drawer';

    @property({ type: Boolean })
    isCategory: boolean = false;

    @property()
    data: UserTag[] | TopicCategory[] = [];

    @property()
    dataAction: Function = () => {};

    @property()
    updateData: Function = () => {};

    @property()
    activeData?: TopicCategory | UserTag;

    @property()
    inactiveButtons: boolean = false;

    @property({type: Boolean})
    showNew: boolean = false;

    @state()
    newDisabled: boolean = false;

    protected render() {
        return html`
            <div class="tags-categories__draggable-container">
                ${this.data.map(
                    (c, index) => html`
                        <button draggable="true"
                                @dragstart="${(e: DragEvent) => this.handleDragStart(e)}"
                                @drop="${(e: DragEvent) => this.handlePillDrop(e)}"
                                @dragenter="${(e: DragEvent) => this.handleDragEnter(e)}"
                                @dragleave="${(e: DragEvent) => this.handleDragLeave(e)}"
                                @dragend="${(e: DragEvent) => this.handleDragEnd(e)}"
                                @dragover="${(e: DragEvent) => cancelDefault(e)}"
                                @click="${() => this.handleClick(c)}"
                                index="${index}"
                                class="tags-categories__item ${c === this.activeData ? 'active' : ''} ${instanceOfTagGroup(c) ? 'tags-categories__item--group' : ''}"
                                ?disabled="${c !== this.activeData && this.inactiveButtons}"
                                style="${!this.isCategory ? `background-color: ${c.icon}; color: ${getVisibleTextColor(c.icon)}`: ''}">
                                ${this.isCategory
                                    ? html ` <img draggable="false" class="tags-categories__item--image" src="${c.icon}" width="14" height="14">`
                                    : html `<mwc-icon class="tags-categories__sell">sell</mwc-icon>
                                            ${instanceOfTagGroup(c) ?
                                                html`<mwc-icon class="tags-categories__sell tags-categories__sell--group">sell</mwc-icon>`
                                                : ''
                                            }
                                    `
                                }
                            ${c.name}
                        </button>
                    `
                )}
                ${this.showNew ? html`
                    <button @click="${this.newClick}" class="tags-categories__item clickable" ?disabled=${this.newDisabled}>
                        <mwc-icon class="tags-categories__sell">add</mwc-icon>
                        <b>New ${this.isCategory ? 'Category' : 'Tag'}</b>
                    </button>
                ` : ''}
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .tags-categories__sell {
                --mdc-icon-size: 14px;
                vertical-align: text-bottom;
            }

            .tags-categories__sell--group {
                margin-left: -11px;
            }

            .clickable {
                cursor: pointer;
            }

            .tags-categories__item {
                background-color: white;
                border: 1px solid lightgray;
                border-radius: 50px;
                padding: 8px 8px;
                margin: 4px;
                cursor: grab;
                filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
            }

            .tags-categories__item.over {
                outline: 2px dotted #666;
            }

            .tags-categories__item.tags-categories__item--group {
                background-color: #EFECF3;
            }

            .tags-categories__item.active {
                border: 3px solid var(--main-primary-color);
            }

            .tags-categories__item--image {
                vertical-align: text-bottom;
            }

            .tags-categories__draggable-container {
                margin: 0.5rem 0px 1rem;
            }
        `;
    }

    handleClick(data: UserTag | TopicCategory) {
        this.dispatchEvent(new CustomEvent('dataAction', {
            bubbles: true,
            composed: true,
            detail: {
                data
            }
        }));
    }

    handleDragStart(e: any) {
        e.target.style.opacity = '0.4';
        e.dataTransfer?.setData('text', e.target?.getAttribute('index'));
        e.dataTransfer.dropEffect = 'move';
    }

    handleDragEnter(e: any) {
        e.target.classList.add('over');
        cancelDefault(e);
    }

    handleDragLeave(e: any) {
        e.target.classList.remove('over');
        cancelDefault(e);
    }

    handleDragEnd(e: any) {
        e.target.style.opacity = '1';
        cancelDefault(e);
    }

    handlePillDrop(e: any) {
        e.target.classList.remove('over');

        const droppedElementIndex = e.dataTransfer?.getData('text');
        const newElementIndex = e.target.getAttribute('index');
        const reorderedData = moveItem(this.data, droppedElementIndex, newElementIndex);

        this.dispatchEvent(new CustomEvent('updateData', {
            bubbles: true,
            composed: true,
            detail: {
                data: reorderedData,
                isCategory: this.isCategory,
            }
        }));

        cancelDefault(e);
    }

    newClick() {
        this.dispatchEvent(new CustomEvent('newClick'));
    }
}
